import React, { useEffect, useMemo, useState } from 'react';
import MetaTags from '@/components/meta/MetaTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { handleMediaUpload } from '@/utils/mediaHandlers';
import CSSHolographicCard from '@/components/drops/CSSHolographicCard';
import HolographicCardViewer from '@/components/drops/HolographicCardViewer';
import { DropsUploadArea } from '@/components/upload/DropsUploadArea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { useSearchParams } from 'react-router-dom';
import { Loader2, TestTube2, Upload, X } from 'lucide-react';
import { fetchUserProfile } from '@/services/profile-service';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { dropCollectionCreateSchema, safeParse } from '@/lib/validation/schemas';

interface RarityConfig { 
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary'; 
  count: number; 
  unit_value_cents: number;
  media_url?: string;
  thumbnail_url?: string;
}

const rarityOrder: Array<RarityConfig['rarity']> = ['common','uncommon','rare','legendary'];
const rarityLabels: Record<RarityConfig['rarity'], string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  legendary: 'Legendary',
};

const DropsCreate: React.FC = () => {
  const { toast } = useToast();
  const { user, isLoading: authLoading, isInitialized } = useAuth();

  const [step, setStep] = useState(1);
  const [search] = useSearchParams();
  const totalSteps = 4;

  // Step 1
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerUrl, setBannerUrl] = useState('');
  const [budgetCents, setBudgetCents] = useState<number>(0);
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [isTest, setIsTest] = useState(false);
  
  // Campaign settings (for production drops)
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [targetImpressions, setTargetImpressions] = useState<number>(0);
  const [pacingMode, setPacingMode] = useState<'auto' | 'manual' | 'paused'>('auto');
  
  // User permissions - computed from secure role system
  // (moved to after useUserRoles hook call)

  // Step 2
  const [rarities, setRarities] = useState<RarityConfig[]>([
    { rarity: 'common', count: 100, unit_value_cents: 10 },
  ]);

  // Step 3
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [splineUrl, setSplineUrl] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'spline'>('image');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isGeneratingHolographic, setIsGeneratingHolographic] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [authorName, setAuthorName] = useState('');
  
  // New state for per-rarity uploads
  const [selectedRarityFiles, setSelectedRarityFiles] = useState<Record<string, File | null>>({});
  const [raritySplineUrls, setRaritySplineUrls] = useState<Record<string, string>>({});
  const [previewRarity, setPreviewRarity] = useState('common');

  // Step 4
  const [error, setError] = useState<string | null>(null);

  // Progress calculation
  const progressValue = useMemo(() => (step / totalSteps) * 100, [step]);

  // Load existing collection if collectionId in URL
  useEffect(() => {
    const cId = search.get('collectionId');
    if (cId) {
      setCollectionId(cId);
      loadExistingCollection(cId);
    }
  }, [search]);

  // Get user roles from secure role system
  const { isAdmin, isPartner } = useUserRoles();
  const isAdminOrPartner = isAdmin || isPartner;
  
  // Load user profile for author name
  useEffect(() => {
    if (user?.id) {
      fetchUserProfile(user.id).then(profile => {
        if (profile) {
          setAuthorName(profile.display_name || profile.username || 'Anonymous');
        }
      }).catch(console.error);
    }
  }, [user?.id]);

  const loadExistingCollection = async (cId: string) => {
    try {
      const { data: collection, error: collectionError } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('id', cId)
        .single();

      if (collectionError) throw collectionError;

      setName(collection.name || '');
      setDescription(collection.description || '');
      setBannerUrl(collection.banner_url || '');
      setBudgetCents(collection.budget_cents || 0);
      setIsTest(collection.is_test || false);

      const { data: rarityData, error: rarityError } = await supabase
        .from('drop_rarities')
        .select('*')
        .eq('collection_id', cId)
        .order('rarity');

      if (rarityError) throw rarityError;

      if (rarityData && rarityData.length > 0) {
        setRarities(rarityData.map(r => ({
          rarity: r.rarity as RarityConfig['rarity'],
          count: r.count,
          unit_value_cents: r.unit_value_cents,
          media_url: r.media_url,
          thumbnail_url: r.thumbnail_url
        })));
      }
    } catch (error: any) {
      console.error('Error loading collection:', error);
      toast({ title: 'Error', description: 'Failed to load existing collection' });
    }
  };

  const ensureCollection = async (): Promise<string> => {
    if (collectionId) return collectionId;

    // SECURITY: Validate collection data before creating
    const validationData = {
      name: name,
      description: description,
      icon_url: bannerUrl || 'https://placeholder.com/icon.png', // Placeholder for validation
      rarities: rarities.map(r => ({
        rarity: r.rarity,
        count: r.count,
        unit_value_cents: r.unit_value_cents,
        media_url: r.media_url || 'https://placeholder.com/media.png',
        thumbnail_url: r.thumbnail_url,
      })),
      is_test: isTest,
    };

    const validationResult = safeParse(dropCollectionCreateSchema, validationData);
    if (!validationResult.success) {
      const error = validationResult as { success: false; error: string };
      throw new Error(error.error);
    }

    // Calculate payment split (100% markup model)
    const rewardsPool = rarities.reduce((sum, r) => sum + (r.count * r.unit_value_cents), 0);
    const platformFee = rewardsPool; // 100% markup
    const totalValue = rewardsPool + platformFee; // 2x rewards pool

    // Upload banner if provided
    let uploadedBannerUrl = bannerUrl;
    if (bannerFile) {
      const fileExt = bannerFile.name.split('.').pop();
      const fileName = `${user!.id}/${Date.now()}_banner.${fileExt}`;
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('drops')
        .upload(fileName, bannerFile);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('drops')
        .getPublicUrl(fileName);
      
      uploadedBannerUrl = publicUrl;
    }

    const { data, error } = await supabase
      .from('drop_collections')
      .insert({
        user_id: user!.id,
        name: name || 'Untitled Drop',
        description: description || null,
        banner_url: uploadedBannerUrl || null,
        budget_cents: isTest ? 0 : budgetCents,
        status: 'draft',
        is_test: isTest,
        total_value_cents: isTest ? 0 : totalValue,
        platform_fee_cents: isTest ? 0 : platformFee,
        rewards_pool_cents: isTest ? 0 : rewardsPool,
        rewards_reserved_cents: isTest ? 0 : rewardsPool
      })
      .select('id')
      .single();

    if (error) throw error;
    setCollectionId(data.id);
    return data.id;
  };

  const handleStep1Continue = async () => {
    if (!name.trim()) {
      setError('Please enter a collection name');
      return;
    }
    // Allow $0 budget for test drops, but require positive budget for production drops
    if (!isTest && budgetCents <= 0) {
      setError('Please set a positive budget');
      return;
    }

    try {
      await ensureCollection();
      setError(null);
      setStep(2);
    } catch (e: any) {
      setError(e.message || 'Failed to save collection');
    }
  };

  const handleStep2Continue = async () => {
    if (rarities.filter(r => r.count > 0).length === 0) {
      setError('Please configure at least one rarity with count > 0');
      return;
    }

    try {
      const id = await ensureCollection();
      
      for (const rarity of rarities) {
        const { error } = await supabase
          .from('drop_rarities')
          .upsert({
            collection_id: id,
            rarity: rarity.rarity,
            count: rarity.count,
            unit_value_cents: rarity.unit_value_cents,
            cost_cents: rarity.count * rarity.unit_value_cents
          }, {
            onConflict: 'collection_id,rarity'
          });
        if (error) throw error;
      }

      setError(null);
      setStep(3);
    } catch (e: any) {
      setError(e.message || 'Failed to save rarities');
    }
  };

  const handleCustomImageSelect = (file: File) => {
    setSelectedFile(file);
  };

  const handleRarityImageSelect = (rarity: string, file: File) => {
    setSelectedRarityFiles(prev => ({ ...prev, [rarity]: file }));
    handleUploadRarityImage(rarity, file);
  };

  const handleUploadRarityImage = async (rarity: string, file: File) => {
    if (!user || !collectionId) return;

    try {
      setIsGeneratingHolographic(true);
      const result = await handleMediaUpload(
        file,
        user.id,
        true, // isPremiumUser
        (progress) => {
          console.log('Upload progress:', progress);
          setUploadProgress(progress.progress);
        },
        true // performModeration
      );

      if (result.url) {
        // Save to drop_rarities table instead of drop_collections
        await supabase
          .from('drop_rarities')
          .update({
            media_url: result.url,
            thumbnail_url: result.thumbnailUrl || result.url
          })
          .eq('collection_id', collectionId)
          .eq('rarity', rarity);

        // Update local state
        setRarities(prev => 
          prev.map(r => 
            r.rarity === rarity 
              ? { ...r, media_url: result.url, thumbnail_url: result.thumbnailUrl || result.url }
              : r
          )
        );

        toast({ title: 'Success', description: `${rarity} media uploaded successfully!` });
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({ title: 'Upload failed', description: error.message || 'Please try again' });
    } finally {
      setIsGeneratingHolographic(false);
      setUploadProgress(0);
    }
  };

  const handleSaveRaritySpline = async (rarity: string) => {
    if (!collectionId || !raritySplineUrls[rarity]) return;

    try {
      await supabase
        .from('drop_rarities')
        .update({
          media_url: raritySplineUrls[rarity],
          thumbnail_url: null
        })
        .eq('collection_id', collectionId)
        .eq('rarity', rarity);

      setRarities(prev => 
        prev.map(r => 
          r.rarity === rarity 
            ? { ...r, media_url: raritySplineUrls[rarity], thumbnail_url: null }
            : r
        )
      );

      toast({ title: 'Success', description: `${rarity} Spline URL saved!` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save Spline URL' });
    }
  };

  const getPreviewUrl = () => {
    const currentRarity = rarities.find(r => r.rarity === previewRarity);
    return currentRarity?.media_url || '';
  };

  const getCurrentPreviewRarity = () => {
    return previewRarity;
  };

  const getCurrentRarityCount = () => {
    const currentRarity = rarities.find(r => r.rarity === previewRarity);
    return currentRarity?.count || 0;
  };

  const canProceedFromStep3 = () => {
    // Check that all rarities with count > 0 have media uploaded
    return rarities.filter(r => r.count > 0).every(r => r.media_url);
  };

  const handleSaveSpline = async () => {
    if (!collectionId || !splineUrl.trim()) return;

    try {
      await supabase
        .from('drop_collections')
        .update({
          media_url: splineUrl,
          media_type: 'spline',
          media_config: { url: splineUrl }
        })
        .eq('id', collectionId);

      setPreviewImageUrl(splineUrl);
      toast({ title: 'Success', description: 'Spline URL saved!' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to save Spline URL' });
    }
  };

  const handleProceedToPayment = async () => {
    try {
      const id = await ensureCollection();
      
      // For test drops, skip payment and mark as ready for review
      if (isTest) {
        const { error } = await supabase
          .from('drop_collections')
          .update({ 
            status: 'pending_review',
            payment_status: 'completed' // Test drops don't require payment
          })
          .eq('id', id);
        
        if (error) throw error;

        // Send pending review email notification
        try {
          const { data: collection } = await supabase
            .from('drop_collections')
            .select('name, user_id')
            .eq('id', id)
            .single();

          const { data: profile } = await supabase
            .from('profiles')
            .select('email')
            .eq('id', collection?.user_id)
            .single();

          if (profile?.email) {
            await supabase.functions.invoke('send-drop-notification', {
              body: {
                collectionId: id,
                status: 'pending_review',
                userEmail: profile.email,
                collectionName: collection?.name || 'Untitled Collection',
              }
            });
          }
        } catch (emailError) {
          console.error('Failed to send pending review email:', emailError);
          // Don't fail the submission if email fails
        }
        
        // Redirect to drops dashboard with success message
        window.location.href = '/drops?submitted=true';
        return;
      }
      
      // For production drops, proceed to Stripe payment
      const { data, error } = await supabase.functions.invoke('create-drop-payment', { body: { collectionId: id } });
      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL');
      window.open(data.url, '_blank');
    } catch (e: any) {
      toast({ title: 'Payment error', description: e.message || 'Failed to start checkout', variant: 'destructive' });
    }
  };

  // Show loading while auth is initializing
  if (!isInitialized || authLoading) {
    return (
      <main className="container mx-auto max-w-4xl px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading...</span>
          </div>
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="container mx-auto max-w-4xl px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <Card>
            <CardContent className="pt-6">
              <p className="text-center">Please log in to create drops.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="container mx-auto max-w-6xl px-4 py-6">
        <MetaTags title="Create Drops – FOMO" description="Set budget, assign rarities, upload media, and pay to submit your Drop for review." />
        <h1 className="text-2xl md:text-3xl font-bold mb-4">Create a Drop Collection</h1>
        <p className="text-muted-foreground mb-6">A simple step-by-step flow in plain English. Configure quantities, value, and media, then pay to submit for review.</p>

        <div className="mb-6">
          <Progress value={progressValue} />
          <div className="text-sm text-muted-foreground mt-2">Step {step} of {totalSteps}</div>
        </div>

        {error && (
          <div className="mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-destructive">{error}</p>
                  <Button variant="outline" size="sm" onClick={() => setError(null)}>
                    Dismiss
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {step === 1 && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Basics & Budget</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 min-h-[400px]">
            <div className="space-y-4 flex-1">
              <div>
                <Label htmlFor="name">Name your Drop Collection</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Neon Collectibles" className="rounded-xl mt-2" />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  placeholder="Describe your drop collection..."
                  className="rounded-xl mt-2 min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tell collectors what makes this drop special
                </p>
              </div>

              <div>
                <Label htmlFor="banner">Banner Image (Optional)</Label>
                <div className="mt-2">
                  {bannerUrl || bannerFile ? (
                    <div className="relative w-full h-32 rounded-xl overflow-hidden border-2 border-border">
                      <img 
                        src={bannerFile ? URL.createObjectURL(bannerFile) : bannerUrl} 
                        alt="Banner preview" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => {
                          setBannerFile(null);
                          setBannerUrl('');
                        }}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-xl cursor-pointer hover:bg-accent/50 transition-colors">
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload banner</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setBannerFile(file);
                        }}
                      />
                    </label>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Recommended: 1200x400px banner for your collection page
                </p>
              </div>

              <div>
                <Label htmlFor="budget">Set a total budget (USD cents)</Label>
                <Input 
                  id="budget" 
                  type="number" 
                  value={isTest ? 0 : budgetCents} 
                  onChange={(e) => setBudgetCents(parseInt(e.target.value || '0'))} 
                  className="rounded-xl mt-2"
                  disabled={isTest}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {isTest ? 'Test drops have $0 value for testing purposes.' : 'Budget helps you reason about quantities. You can adjust later.'}
                </p>
              </div>
              
              {/* Test Drop Toggle (Admin/Partner only) */}
              {isAdminOrPartner && (
                <div className="border border-orange-500/30 bg-orange-500/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TestTube2 className="w-5 h-5 text-orange-500" />
                      <div>
                        <Label htmlFor="test-drop" className="text-base font-medium cursor-pointer">
                          Test Drop Mode
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          For testing end-to-end flow with admin/partner visibility only
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="test-drop"
                      checked={isTest}
                      onCheckedChange={(checked) => {
                        setIsTest(checked);
                        if (checked) {
                          setBudgetCents(0);
                        }
                      }}
                    />
                  </div>
                  {isTest && (
                    <div className="text-xs text-orange-600/80 bg-orange-500/10 rounded-lg p-3 border border-orange-500/20">
                      <strong>Test Mode Active:</strong> This drop will have $0 value and only be visible to admin and partner users. Perfect for testing the complete drop flow including Stripe integration.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-6 mt-auto">
              <div></div>
              <Button onClick={handleStep1Continue} className="rounded-xl">Save & Continue</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Configure Rarities</h2>
            <p className="text-muted-foreground">Set up your rarity tiers and item counts</p>
          </div>
          
          <div className="grid gap-4">
            {rarityOrder.map((rarityType) => {
              const rarity = rarities.find(r => r.rarity === rarityType) || { rarity: rarityType, count: 0, unit_value_cents: 10 };
              const rarityIndex = rarities.findIndex(r => r.rarity === rarityType);

              const rarityConfig = {
                common: {
                  bgGradient: 'from-emerald-500/20 to-green-600/20',
                  borderColor: 'border-emerald-500/40',
                  badgeBg: 'bg-emerald-500/20',
                  badgeText: 'text-emerald-300',
                  badgeBorder: 'border-emerald-500/40'
                },
                uncommon: {
                  bgGradient: 'from-blue-500/20 to-cyan-600/20',
                  borderColor: 'border-blue-500/40',
                  badgeBg: 'bg-blue-500/20',
                  badgeText: 'text-blue-300',
                  badgeBorder: 'border-blue-500/40'
                },
                rare: {
                  bgGradient: 'from-purple-500/20 to-pink-600/20',
                  borderColor: 'border-purple-500/40',
                  badgeBg: 'bg-purple-500/20',
                  badgeText: 'text-purple-300',
                  badgeBorder: 'border-purple-500/40'
                },
                legendary: {
                  bgGradient: 'from-orange-500/20 to-yellow-600/20',
                  borderColor: 'border-orange-500/40',
                  badgeBg: 'bg-orange-500/20',
                  badgeText: 'text-orange-300',
                  badgeBorder: 'border-orange-500/40'
                }
              };

              const config = rarityConfig[rarityType];

              return (
                <Card key={rarityType} className={`relative overflow-hidden border-2 ${config.borderColor} bg-gradient-to-br ${config.bgGradient} backdrop-blur-sm`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full border ${config.badgeBorder} ${config.badgeBg}`}>
                          <span className={`text-sm font-medium ${config.badgeText}`}>
                            {rarityLabels[rarityType]}
                          </span>
                        </div>
                        <div className={`px-2 py-1 rounded text-xs ${config.badgeBg} ${config.badgeText}`}>
                          {rarity.count} items
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Total Value</div>
                        <div className="text-lg font-bold">
                          ${((rarity.count * rarity.unit_value_cents) / 100).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Quantity</Label>
                        <Input
                          type="number"
                          value={rarity.count}
                          min={0}
                          step={1}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value, 10);
                            const count = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                            if (rarityIndex >= 0) {
                              setRarities(prev => prev.map((r, i) => i === rarityIndex ? { ...r, count } : r));
                            } else {
                              setRarities(prev => [...prev, { rarity: rarityType, count, unit_value_cents: 10 }]);
                            }
                          }}
                          className="bg-background/50 border-border/50"
                        />
                        <div className="text-xs text-muted-foreground">Enter any count your campaign requires.</div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Unit Value (cents)</Label>
                        <Input
                          type="number"
                          value={rarity.unit_value_cents}
                          onChange={(e) => {
                            const unit_value_cents = parseInt(e.target.value) || 10;
                            if (rarityIndex >= 0) {
                              setRarities(prev => prev.map((r, i) => i === rarityIndex ? { ...r, unit_value_cents } : r));
                            } else {
                              setRarities(prev => [...prev, { rarity: rarityType, count: 0, unit_value_cents }]);
                            }
                          }}
                          className="bg-background/50 border-border/50"
                          min="1"
                          step="1"
                        />
                        <div className="text-xs text-muted-foreground">
                          ${(rarity.unit_value_cents / 100).toFixed(2)} per item
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-6">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">
              Back
            </Button>
            <Button onClick={handleStep2Continue} className="rounded-xl">
              Save & Continue
            </Button>
          </div>
        </div>
      )}

        {step === 3 && (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>Upload Media Assets</CardTitle>
              <p className="text-muted-foreground text-sm">Upload media for each rarity. Higher tiers require previous tiers to be uploaded first.</p>
            </CardHeader>
            <CardContent className="space-y-6 min-h-[400px]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  {/* Media Type Selection */}
                  <div>
                    <Label>Choose Media Type</Label>
                    <Select value={mediaType} onValueChange={(value) => setMediaType(value as 'image' | 'spline')}>
                      <SelectTrigger className="rounded-xl mt-2">
                        <SelectValue placeholder="Select media type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Image Upload</SelectItem>
                        <SelectItem value="spline">3D Spline Embed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Per-Rarity Upload Sections */}
                  <div className="space-y-4">
                    {['common', 'uncommon', 'rare', 'legendary'].map((rarity) => {
                      const rarityConfig = rarities.find(r => r.rarity === rarity);
                      if (!rarityConfig || rarityConfig.count === 0) return null;

                      const canUpload = rarity === 'common' || 
                        (rarity === 'uncommon' && rarities.find(r => r.rarity === 'common')?.media_url) ||
                        (rarity === 'rare' && rarities.find(r => r.rarity === 'uncommon')?.media_url) ||
                        (rarity === 'legendary' && rarities.find(r => r.rarity === 'rare')?.media_url);

                      const getDisabledMessage = () => {
                        if (rarity === 'uncommon') return 'Upload common tier first';
                        if (rarity === 'rare') return 'Upload uncommon tier first';
                        if (rarity === 'legendary') return 'Upload rare tier first';
                        return undefined;
                      };

                      if (mediaType === 'image') {
                        return (
                          <DropsUploadArea
                            key={rarity}
                            rarity={rarity as 'common' | 'uncommon' | 'rare' | 'legendary'}
                            count={rarityConfig.count}
                            onFileSelect={(file) => handleRarityImageSelect(rarity, file)}
                            isUploaded={!!rarityConfig.media_url}
                            isUploading={isGeneratingHolographic && selectedRarityFiles[rarity] !== null}
                            uploadProgress={uploadProgress}
                            disabled={!canUpload}
                            disabledMessage={getDisabledMessage()}
                          />
                        );
                      }

                      // Spline UI (keeping original for now)
                      return (
                        <div key={rarity} className="border rounded-xl p-4 bg-gradient-to-br from-muted/20 to-muted/10">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium capitalize">{rarity}</h3>
                              <span className="text-xs bg-background px-2 py-1 rounded-full border">
                                {rarityConfig.count} items
                              </span>
                            </div>
                            {rarityConfig.media_url && (
                              <span className="text-xs text-green-600 font-medium">✓ Saved</span>
                            )}
                          </div>

                          {!canUpload ? (
                            <p className="text-xs text-muted-foreground mb-2">
                              {getDisabledMessage()}
                            </p>
                          ) : (
                            <div className="space-y-2">
                              <Input 
                                type="url"
                                value={raritySplineUrls[rarity] || ''}
                                onChange={(e) => setRaritySplineUrls(prev => ({...prev, [rarity]: e.target.value}))}
                                placeholder="https://my.spline.design/..."
                                className="rounded-xl text-sm"
                              />
                              <Button 
                                onClick={() => handleSaveRaritySpline(rarity)}
                                disabled={!raritySplineUrls[rarity]?.trim()}
                                size="sm"
                                className="w-full rounded-xl"
                              >
                                Save {rarity} Spline
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="lg:sticky lg:top-4">
                  <Label>Live Preview</Label>
                  <div className="mt-2 max-w-[200px] mx-auto">
                    <HolographicCardViewer
                      imageUrl={getPreviewUrl()}
                      isGenerating={isGeneratingHolographic}
                      overlayData={{
                        author: authorName,
                        title: name,
                        serial: `1/${getCurrentRarityCount()}`,
                        rarity: getCurrentPreviewRarity()
                      }}
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <p className="text-xs text-muted-foreground mb-2">
                      Previewing: {getCurrentPreviewRarity()}
                    </p>
                    <div className="flex justify-center gap-1">
                      {['common', 'uncommon', 'rare', 'legendary'].map(rarity => {
                        const config = rarities.find(r => r.rarity === rarity);
                        if (!config || config.count === 0) return null;
                        return (
                          <button
                            key={rarity}
                            onClick={() => setPreviewRarity(rarity)}
                            className={`text-xs px-2 py-1 rounded-full capitalize transition-colors ${
                              previewRarity === rarity 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                          >
                            {rarity}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-6 mt-auto">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl">
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(4)} 
                  disabled={!canProceedFromStep3()}
                  className="rounded-xl"
                >
                  Continue to Review
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      {step === 4 && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <h2 className="text-2xl font-bold">Review & Submit</h2>
              {isTest && (
                <div className="px-3 py-1 bg-orange-500/20 border border-orange-500 rounded-full flex items-center gap-2">
                  <TestTube2 className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-bold text-orange-500">TEST MODE</span>
                </div>
              )}
            </div>
            <p className="text-muted-foreground">
              {isTest 
                ? 'Test drop for development - $0 value, admin/partner visibility only' 
                : 'Review your collection details and submit for approval'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left side - Collection Details */}
            <div className="space-y-6">
              {/* Collection Info Card */}
              <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    Collection Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{name}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                    <span className="text-muted-foreground">Budget</span>
                    <span className="font-medium">${isTest ? '0.00' : (budgetCents / 100).toFixed(2)}</span>
                  </div>
                  {isTest && (
                    <div className="flex items-center gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <TestTube2 className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-orange-600">Test Drop - $0 Value</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Rarities Breakdown Card */}
              <Card className="bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-2 h-2 bg-secondary rounded-full"></div>
                    Rarity Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {rarities.filter(r => r.count > 0).map(rarity => {
                    const rarityColors = {
                      common: 'border-emerald-500/30 bg-emerald-500/10',
                      uncommon: 'border-blue-500/30 bg-blue-500/10', 
                      rare: 'border-purple-500/30 bg-purple-500/10',
                      legendary: 'border-orange-500/30 bg-orange-500/10'
                    };
                    
                    return (
                      <div key={rarity.rarity} className={`p-3 rounded-lg border ${rarityColors[rarity.rarity as keyof typeof rarityColors]}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium capitalize">{rarity.rarity}</span>
                          <span className="font-bold">
                            ${isTest ? '0.00' : ((rarity.count * rarity.unit_value_cents) / 100).toFixed(2)}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {rarity.count} items × ${isTest ? '0.00' : (rarity.unit_value_cents / 100).toFixed(2)} each
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Payment Summary Card */}
              <Card className="bg-gradient-to-br from-green-500/5 to-green-600/10 border-green-500/20">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        {isTest ? 'Test Payment (End-to-End Test)' : 'Total to Pay'}
                      </div>
                      <div className="text-3xl font-bold text-green-600">
                        ${isTest 
                          ? '0.00' 
                          : ((rarities.reduce((sum, r) => sum + (r.count * r.unit_value_cents), 0) * 2) / 100).toFixed(2)
                        }
                      </div>
                      {isTest && (
                        <div className="text-xs text-orange-600 mt-2">
                          Test mode: Stripe checkout will process $0 transaction
                        </div>
                      )}
                    </div>
                    
                    {!isTest && (
                      <div className="space-y-3 pt-4 border-t">
                        <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Rewards Pool (to users)</span>
                          <span className="font-semibold text-blue-600">
                            ${(rarities.reduce((sum, r) => sum + (r.count * r.unit_value_cents), 0) / 100).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-background/50 rounded-lg">
                          <span className="text-sm text-muted-foreground">Platform Fee</span>
                          <span className="font-semibold text-orange-600">
                            ${(rarities.reduce((sum, r) => sum + (r.count * r.unit_value_cents), 0) / 100).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-background/50 rounded-lg p-3">
                      <div className="text-sm text-muted-foreground mb-1">Recipients</div>
                      <div className="text-lg font-semibold">
                        {rarities.reduce((sum, r) => sum + r.count, 0)} users will receive drops
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Each user receives the full unit value when claiming
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right side - Live Preview */}
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                    Live Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <div className="max-w-[280px] w-full mb-4">
                    <HolographicCardViewer
                      imageUrl={getPreviewUrl()}
                      overlayData={{
                        author: authorName,
                        title: name,
                        serial: `1/${getCurrentRarityCount()}`,
                        rarity: getCurrentPreviewRarity()
                      }}
                    />
                  </div>
                  
                  <div className="text-center space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Previewing: <span className="capitalize font-medium">{getCurrentPreviewRarity()}</span>
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-2">
                      {['common', 'uncommon', 'rare', 'legendary'].map(rarity => {
                        const config = rarities.find(r => r.rarity === rarity);
                        if (!config || config.count === 0) return null;
                        
                        const rarityColors = {
                          common: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                          uncommon: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
                          rare: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
                          legendary: 'bg-orange-500/20 text-orange-300 border-orange-500/40'
                        };
                        
                        return (
                          <button
                            key={rarity}
                            onClick={() => setPreviewRarity(rarity)}
                            className={`px-3 py-1.5 rounded-full capitalize text-sm font-medium border transition-all hover:scale-105 ${
                              previewRarity === rarity 
                                ? rarityColors[rarity as keyof typeof rarityColors]
                                : 'bg-muted/20 text-muted-foreground border-muted/40 hover:bg-muted/30'
                            }`}
                          >
                            {rarity}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex items-center justify-between pt-6">
            <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl">
              Back
            </Button>
            <Button 
              onClick={handleProceedToPayment} 
              className="rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      )}
    </main>
  );
};

export default DropsCreate;